/**
 * AI Hiring Agent API Service
 * Handles the agentic conversation flow with copilot endpoints
 */

// Cache for API responses
const responseCache = new Map();

/**
 * Detects if user input is a complete Job Description using copilot-custom
 * @param {string} userInput - The user's input text
 * @param {string} joid - Job ID for the request
 * @returns {Promise<boolean>} - true if input is a JD, false otherwise
 */
export const detectJobDescription = async (userInput, joid = 'default-job-id') => {
  const cacheKey = `detect-jd-${userInput.substring(0, 100)}`;
  
  if (responseCache.has(cacheKey)) {
    console.log('Using cached JD detection result');
    return responseCache.get(cacheKey);
  }

  try {
    const payload = {
      joid,
      custom_chat_info: {
        custom_purpose: "detect-jd",
        system_msg: "You are a recruiter assistant",
        base_msg: `${userInput}. Can you tell me if this text is a complete Job Description or not. Please give response in boolean true or false`,
        enforce_json: false
      }
    };

    const response = await fetch('https://pub.findem.ai/pub/api/copilot-custom', {
      method: 'POST',
      credentials: 'include',
      headers: { 'Content-Type': 'application/json' },
      body: JSON.stringify(payload),
      mode: 'cors'
    });

    if (!response.ok) {
      throw new Error(`JD detection failed: ${response.status} ${response.statusText}`);
    }

    const result = await response.json();
    // Parse the response to extract boolean
    let isJD = false;
    if (result && result.custom_chat_response && result.custom_chat_response.content) {
      const responseText = result.custom_chat_response?.content?.toLowerCase().trim();
      isJD = responseText.includes('true') || responseText === 'true';
    }

    console.log('JD Detection result:', { input: userInput.substring(0, 50), isJD });
    
    responseCache.set(cacheKey, isJD);
    return isJD;
  } catch (error) {
    console.error('Error detecting JD:', error);
    // Fallback: simple heuristic
    return userInput.length > 200 && 
           (userInput.toLowerCase().includes('job description') || 
            userInput.toLowerCase().includes('responsibilities') ||
            userInput.toLowerCase().includes('requirements'));
  }
};

/**
 * Processes Job Description using copilot-jd-searchgen
 * @param {string} jdText - The full JD text (accumulated user messages)
 * @param {string} joid - Job ID for the request
 * @returns {Promise<Object>} - API response with search_gen_response
 */
export const processJobDescription = async (jdText, joid = 'default-job-id') => {
  // const cacheKey = `process-jd-${jdText.substring(0, 100)}`;
  
  // if (responseCache.has(cacheKey)) {
  //   console.log('Using cached JD processing result');
  //   return responseCache.get(cacheKey);
  // }

  try {
    const payload = {
      jd_search_gen_info: {
        verbose: false,
        jd_source: jdText
      },
      joid,
      reset_context: true
    };

    console.log('Processing JD with payload:', {
      jd_length: jdText.length,
      joid,
      reset_context: true
    });

    const response = await fetch('https://search.findem.ai/pub/api/copilot-jd-searchgen', {
      method: 'POST',
      credentials: 'include',
      headers: { 'Content-Type': 'application/json' },
      body: JSON.stringify(payload),
      mode: 'cors'
    });

    if (!response.ok) {
      throw new Error(`JD processing failed: ${response.status} ${response.statusText}`);
    }

    let result = await response.json();
    
    // Normalize response structure
    if (!result.search_gen_response && result.jd_search_gen_response) {
      result.search_gen_response = result.jd_search_gen_response;
    }

    console.log('JD Processing result:', {
      context_id: result.Cxt_id,
      response_type: result.Response_type,
      has_requirements: !!result.search_gen_response?.icp_requirements,
      requirements_count: result.search_gen_response?.icp_requirements?.length || 0
    });

    // responseCache.set(cacheKey, result);
    return result;
  } catch (error) {
    console.error('Error processing JD:', error);
    throw error;
  }
};

/**
 * Processes conversational requirements using copilot-searchgen
 * @param {Array<string>} userMessages - All user messages in the conversation
 * @param {string} joid - Job ID for the request
 * @returns {Promise<Object>} - API response with search_gen_response
 */
export const processConversationalRequirements = async (userMessages, joid = 'default-job-id') => {
  // const allUserText = userMessages.join(' ');
  // const cacheKey = `process-conv-${allUserText.substring(0, 100)}`;
  
  // if (responseCache.has(cacheKey)) {
  //   console.log('Using cached conversational processing result');
  //   return responseCache.get(cacheKey);
  // }

  try {
    const payload = {
      qna_search_gen_info: {
        questions: userMessages,
        v2: true
      },
      joid,
      reset_context: true
    };

    console.log('Processing conversational requirements with payload:', {
      messages_count: userMessages.length,
      joid,
      reset_context: true
    });

    const response = await fetch('https://pub.findem.ai/pub/api/copilot-searchgen', {
      method: 'POST',
      credentials: 'include',
      headers: { 'Content-Type': 'application/json' },
      body: JSON.stringify(payload),
      mode: 'cors'
    });

    if (!response.ok) {
      throw new Error(`Conversational processing failed: ${response.status} ${response.statusText}`);
    }

    const result = await response.json();

    console.log('Conversational Processing result:', {
      context_id: result.Cxt_id,
      response_type: result.Response_type,
      has_requirements: !!result.search_gen_response?.icp_requirements,
      requirements_count: result.search_gen_response?.icp_requirements?.length || 0
    });

    // responseCache.set(cacheKey, result);
    return result;
  } catch (error) {
    console.error('Error processing conversational requirements:', error);
    throw error;
  }
};

/**
 * Formats requirements into a readable summary for the AI assistant
 * @param {Object} searchGenResponse - The search_gen_response from API
 * @returns {string} - Formatted requirements summary
 */
export const formatRequirementsSummary = (searchGenResponse) => {
  if (!searchGenResponse || !searchGenResponse.icp_requirements) {
    return 'No specific requirements detected.';
  }

  const { icp_requirements, search_name, skill_keywords, keyword_titles } = searchGenResponse;
  
  let summary = [];
  
  if (search_name) {
    summary.push(`**Position:** ${search_name}`);
  }
  
  if (keyword_titles && keyword_titles.length > 0) {
    summary.push(`**Role Titles:** ${keyword_titles.join(', ')}`);
  }
  
  if (skill_keywords && skill_keywords.length > 0) {
    summary.push(`**Key Skills:** ${skill_keywords.slice(0, 8).join(', ')}`);
  }

  // Group requirements by section
  const sections = {};
  icp_requirements.forEach(req => {
    if (!sections[req.section_name]) {
      sections[req.section_name] = { mustHave: [], niceToHave: [] };
    }
    
    if (req.section_type === 'Must Have' || req.section_type === 'MH') {
      sections[req.section_name].mustHave.push(req);
    } else {
      sections[req.section_name].niceToHave.push(req);
    }
  });

  // Add section summaries
  Object.keys(sections).forEach(sectionName => {
    const section = sections[sectionName];
    if (section.mustHave.length > 0) {
      summary.push(`**${sectionName} (Required):** ${section.mustHave.length} criteria`);
    }
    if (section.niceToHave.length > 0) {
      summary.push(`**${sectionName} (Preferred):** ${section.niceToHave.length} criteria`);
    }
  });

  return summary.join('\n');
};

/**
 * Fetch real candidate matches using the matchesV2 API
 * @param {Array} icpRequirements - ICP requirements from search_gen_response
 * @param {string} joid - Job ID for the request
 * @param {number} count - Number of candidates to fetch (default: 20)
 * @param {Object} macrosByCategories - Dictionary of macro definitions by ID
 * @returns {Promise<Array>} - Array of candidate objects
 */
export const fetchCandidateMatches = async (icpRequirements, joid = 'default-job-id', count = 20, macrosByCategories = {}) => {
  // const cacheKey = `matches-${JSON.stringify(icpRequirements).substring(0, 100)}-${count}`;
  
  // if (responseCache.has(cacheKey)) {
  //   console.log('Using cached candidate matches');
  //   return responseCache.get(cacheKey);
  // }

  try {
    const payload = {
      joid,
      requirements: icpRequirements || [],
      detail: true,
      new_algo: false,
      new_service: true,
      skinny_output: false,
      count,
      start_index: 0,
      crm_cxt: false,
      use_optional_queries: true,
      sandbox: false,
      include_stale: false,
      prioritize_freshness: true
    };

    console.log('Fetching candidate matches with payload:', {
      joid,
      requirements_count: icpRequirements?.length || 0,
      count
    });

    const response = await fetch('https://search.findem.ai/pub/api/matchesV2', {
      method: 'POST',
      credentials: 'include',
      headers: { 
        'Content-Type': 'application/json',
        'accept': '*/*'
      },
      body: JSON.stringify(payload),
      mode: 'cors'
    });

    if (!response.ok) {
      throw new Error(`Candidate matching failed: ${response.status} ${response.statusText}`);
    }

    const result = await response.json();
    
    // Process the profiles from prs_by_id, filtering out duplicates
    const candidates = processCandidateProfiles(
      result.prs_by_id || {}, 
      result.dup_prids || [],
      result.query_results || [],
      result.search_cxt || {},
      icpRequirements || [],
      macrosByCategories
    );
    
    console.log('Raw API Response:', {
      total_matches: result.total_matches,
      profiles_count: Object.keys(result.prs_by_id || {}).length,
      query_results_count: (result.query_results || []).length,
      duplicates_count: (result.dup_prids || []).length,
      has_search_cxt: !!result.search_cxt,
      processed_candidates: candidates.length
    });

    console.log('First few profile IDs:', Object.keys(result.prs_by_id || {}).slice(0, 3));
    console.log('Sample profile structure:', Object.values(result.prs_by_id || {})[0]);

    // If no candidates were processed from API, use mock data
    if (candidates.length === 0 && Object.keys(result.prs_by_id || {}).length === 0) {
      console.log('No profiles in API response, using mock candidates');
      const mockCandidates = generateMockCandidates({ skill_keywords: ['JavaScript', 'React'], keyword_titles: ['Software Engineer'] });
      return {
        candidates: mockCandidates,
        total_matches: result.total_matches || mockCandidates.length,
        processed_count: mockCandidates.length
      };
    }

    // Return both candidates and total count
    const response_data = {
      candidates,
      total_matches: result.total_matches || 0,
      processed_count: candidates.length
    };

    // responseCache.set(cacheKey, response_data);
    return response_data;
  } catch (error) {
    console.error('Error fetching candidate matches:', error);
    // Return mock candidates as fallback in same format
    const mockCandidates = generateMockCandidates({ skill_keywords: ['JavaScript', 'React'], keyword_titles: ['Software Engineer'] });
    console.log('Using mock candidates due to API error:', mockCandidates);
    return {
      candidates: mockCandidates,
      total_matches: mockCandidates.length,
      processed_count: mockCandidates.length
    };
  }
};

/**
 * Process raw profile data from matchesV2 API into candidate format
 * @param {Object} profilesById - The prs_by_id object from API response
 * @param {Array} dupPrids - Array of duplicate profile IDs to filter out
 * @param {Array} queryResults - Array of QueryResult objects with profile scores
 * @param {Object} searchCxt - Search context with auto_macro_map and Search_cxt
 * @param {Array} requirements - Original ICP requirements with bool_str
 * @param {Object} macrosByCategories - Dictionary of macro definitions by ID
 * @returns {Array} - Array of processed candidate objects
 */
export const processCandidateProfiles = (profilesById, dupPrids = [], queryResults = [], searchCxt = {}, requirements = [], macrosByCategories = {}) => {
  const candidates = [];
  const duplicateIds = new Set(dupPrids);
  
  Object.entries(profilesById).forEach(([profileId, profile]) => {
    // Skip duplicate profiles
    if (duplicateIds.has(profileId)) {
      console.log(`Skipping duplicate profile: ${profileId}`);
      return;
    }
    try {
      const candidate = profile.candidate || {};
      const workexp = profile.workexp || [];
      const education = profile.education || [];
      
      // Get current position
      const currentWork = workexp.find(exp => exp.duration?.to_present) || workexp[0];
      const currentTitle = currentWork?.projects?.[0]?.role_and_group?.title || 'Professional';
      const currentCompany = currentWork?.company?.name || '';
      
      // Calculate experience years (handling overlaps)
      const experienceYears = calculateExperienceYears(workexp);
      
      // Extract matched skills from requirements (skills_mh and fmmagic sections)
      const skills = extractMatchedSkills(profileId, queryResults, searchCxt, requirements, macrosByCategories);
      
      // Extract education info for additional context
      const latestEducation = education.find(edu => edu.education_details?.degree?.length > 0);
      const degree = latestEducation?.education_details?.degree?.[0];
      const major = latestEducation?.education_details?.major?.[0];
      
      // Calculate match score based on requirements matching
      const matchScore = calculateMatchScore(profileId, queryResults, searchCxt, requirements);
      
      // Debug logging for empty profiles
      console.log('Processing candidate:', {
        profileId,
        fullName: candidate.full_name,
        currentTitle,
        currentCompany,
        skillsCount: skills.length,
        matchScore,
        hasWorkexp: workexp.length > 0,
        hasEducation: education.length > 0
      });

      const processedCandidate = {
        id: profileId,
        name: candidate.full_name || candidate.name || `Candidate ${profileId.substring(0, 8)}`,
        title: currentTitle || 'Professional',
        company: currentCompany || 'Not specified',
        experience: experienceYears > 0 ? `${Math.round(experienceYears)} years` : 'Not specified',
        industry: getIndustryFromCompany(currentCompany),
        matchScore: Math.round(Math.min(99, Math.max(0, matchScore))) || 75,
        skills: skills.length > 0 ? skills.slice(0, 5) : ['No specific skills matched'],
        location: formatLocation(candidate.location) || 'Location not specified',
        avatar: null, // No avatar in API response
        linkedin: candidate.linkedin || null,
        education: degree && major ? `${degree} in ${major}` : degree || major || 'Education not specified',
        profileRef: profileId,
        rawProfile: profile, // Store for detailed view
        matchingMacros: queryResults.find(qr => qr.profile_id === profileId)?.macro_ids || []
      };
      
      candidates.push(processedCandidate);
    } catch (error) {
      console.error('Error processing candidate profile:', profileId, error);
    }
  });
  
  // Sort candidates by query_results order instead of match score
  const queryResultsOrder = queryResults.map(qr => qr.profile_id);
  const sortedCandidates = candidates.sort((a, b) => {
    const indexA = queryResultsOrder.indexOf(a.id);
    const indexB = queryResultsOrder.indexOf(b.id);
    
    // If both found in query_results, sort by their position
    if (indexA !== -1 && indexB !== -1) {
      return indexA - indexB;
    }
    // If only one found, prioritize the one in query_results
    if (indexA !== -1) return -1;
    if (indexB !== -1) return 1;
    // If neither found, maintain current order
    return 0;
  });
  
  return sortedCandidates;
};

/**
 * Helper function to determine industry from company name
 * @param {string} companyName - Company name
 * @returns {string} - Industry category
 */
const getIndustryFromCompany = (companyName) => {
  if (!companyName) return 'Unknown';
  
  const company = companyName.toLowerCase();
  
  if (company.includes('google') || company.includes('microsoft') || company.includes('apple') || 
      company.includes('amazon') || company.includes('meta') || company.includes('facebook')) {
    return 'Big Tech';
  }
  if (company.includes('bank') || company.includes('financial') || company.includes('capital') ||
      company.includes('investment') || company.includes('credit')) {
    return 'Finance';
  }
  if (company.includes('health') || company.includes('medical') || company.includes('pharma') ||
      company.includes('biotech')) {
    return 'Healthcare';
  }
  if (company.includes('retail') || company.includes('commerce') || company.includes('shop')) {
    return 'E-commerce';
  }
  
  return 'Technology';
};

/**
 * Helper function to format location
 * @param {Object} location - Location object from profile
 * @returns {string} - Formatted location string
 */
const formatLocation = (location) => {
  if (!location) return 'Remote';
  
  const parts = [];
  if (location.city) parts.push(location.city);
  if (location.state) parts.push(location.state);
  if (location.country && location.country !== 'United States') parts.push(location.country);
  
  return parts.length > 0 ? parts.join(', ') : 'Remote';
};

/**
 * Mock candidate generation for fallback scenarios
 * @param {Object} searchGenResponse - The search_gen_response from API
 * @returns {Array} - Array of candidate objects
 */
export const generateMockCandidates = (searchGenResponse) => {
  // Extract skills and job titles for mock data generation
  const skills = searchGenResponse?.skill_keywords || ['JavaScript', 'React', 'Node.js'];
  const titles = searchGenResponse?.keyword_titles || ['Software Engineer'];
  
  const candidates = [
    {
      id: 'mock-1',
      name: 'Sarah Chen',
      title: titles[0] || 'Software Engineer',
      company: 'Tech Startup',
      experience: '5 years',
      industry: 'Technology',
      matchScore: 95,
      skills: skills.slice(0, 3),
      location: 'San Francisco, CA',
      avatar: null,
      education: 'BS Computer Science',
      profileRef: 'mock-1'
    },
    {
      id: 'mock-2',
      name: 'Michael Rodriguez',
      title: 'Full Stack Developer',
      company: 'Fintech Corp',
      experience: '4 years',
      industry: 'Fintech',
      matchScore: 88,
      skills: skills.slice(1, 4),
      location: 'New York, NY',
      avatar: null,
      education: 'MS Software Engineering',
      profileRef: 'mock-2'
    },
    {
      id: 'mock-3',
      name: 'Emily Johnson',
      title: 'Frontend Developer',
      company: 'E-commerce Inc',
      experience: '3 years',
      industry: 'E-commerce',
      matchScore: 82,
      skills: skills.slice(0, 2),
      location: 'Austin, TX',
      avatar: null,
      education: 'BS Information Systems',
      profileRef: 'mock-3'
    }
  ];

  return candidates;
};

/**
 * Extract matched skills from requirements bool_str attributes for skills_mh and fmmagic sections
 * @param {string} profileId - Profile ID to extract skills for
 * @param {Array} queryResults - Array of QueryResult objects from matchesV2 API
 * @param {Object} searchCxt - Search context containing auto_macro_map
 * @param {Array} requirements - Original ICP requirements with bool_str and section_id
 * @param {Object} macrosByCategories - Dictionary of macro definitions by ID
 * @returns {Array} - Array of matched skill names
 */
const extractMatchedSkills = (profileId, queryResults, searchCxt, requirements, macrosByCategories = {}) => {
  try {
    const skills = new Set();
    
    // Find the query result for this profile
    const profileResult = queryResults.find(result => result.profile_id === profileId);
    if (!profileResult || !profileResult.macro_ids) {
      return [];
    }

    // Get auto_macro_map from search context
    const autoMacroMap = searchCxt.auto_macro_map || {};

    // Filter requirements for skills_mh and fmmagic sections
    const skillRequirements = requirements.filter(req => 
      req.section_id === 'skills_mh' || req.section_id === 'fmmagic'
    );

    // For each macro_id that matched for this profile
    profileResult.macro_ids.forEach(macroId => {
      if (macroId.startsWith('_auto_mm_')) return;
      
      const _id = macroId.startsWith('Optional:') ? macroId.split(':').at(-1).trim() : macroId.split(':').at(0).trim();
      // Find the requirement that matches this bool_str
      const _boolStr = skillRequirements.map(req => req.bool_str).join(' ');
      if (_boolStr.includes(_id)) {
        skills.add(macrosByCategories[_id] ? macrosByCategories[_id].name : _id);
      }
    });

    return Array.from(skills);
  } catch (error) {
    console.error('Error extracting matched skills for profile', profileId, error);
    return [];
  }
};

/**
 * Extract attributes (skills) from a requirements bool_str
 * @param {string} boolStr - The boolean string containing attribute references
 * @param {Object} macrosByCategories - Dictionary of macro definitions by ID
 * @returns {Array} - Array of skill names
 */
const extractAttributesFromBoolStr = (boolStr, macrosByCategories = {}) => {
  const skills = new Set();
  
  if (!boolStr) return [];
  
  try {
    // Extract all potential attribute references from the bool_str
    // Look for patterns like: id references, quoted strings, etc.
    
    // Extract macro IDs (assuming they might be in format like macro_12345 or just IDs)
    const macroIdMatches = boolStr.match(/\b[a-f0-9]{24}\b|macro_\w+|\b\d+\b/gi);
    if (macroIdMatches) {
      macroIdMatches.forEach(id => {
        const macro = macrosByCategories[id];
        if (macro && macro.name) {
          skills.add(macro.name);
        }
      });
    }
    
    // Extract quoted strings as potential skill names
    const quotedMatches = boolStr.match(/"([^"]*)"/g);
    if (quotedMatches) {
      quotedMatches.forEach(quoted => {
        const skillName = quoted.replace(/"/g, '').trim();
        if (skillName && skillName.length > 1) {
          skills.add(skillName);
        }
      });
    }
    
    // Extract single quoted strings
    const singleQuotedMatches = boolStr.match(/'([^']*)'/g);
    if (singleQuotedMatches) {
      singleQuotedMatches.forEach(quoted => {
        const skillName = quoted.replace(/'/g, '').trim();
        if (skillName && skillName.length > 1) {
          skills.add(skillName);
        }
      });
    }
    
    // Look for common skill patterns in unquoted text
    const skillPatterns = /\b(React|Angular|Vue|Node\.js|Python|Java|JavaScript|TypeScript|AWS|Docker|Kubernetes|MongoDB|PostgreSQL|Redis|GraphQL|REST|API|Machine Learning|AI|Data Science|DevOps|Cloud|Microservices|CI\/CD|Agile|Scrum|HTML|CSS|SQL|NoSQL|Git|Jenkins|Terraform|Spring|Django|Flask|Express|Laravel)\b/gi;
    const patternMatches = boolStr.match(skillPatterns);
    if (patternMatches) {
      patternMatches.forEach(skill => skills.add(skill));
    }
    
  } catch (error) {
    console.error('Error extracting attributes from bool_str:', boolStr, error);
  }
  
  return Array.from(skills);
};

/**
 * Calculate match score based on requirements matching from QueryResult and SearchCxt
 * @param {string} profileId - Profile ID to calculate score for
 * @param {Array} queryResults - Array of QueryResult objects from matchesV2 API
 * @param {Object} searchCxt - Search context containing auto_macro_map and Search_cxt
 * @param {Array} requirements - Original ICP requirements array
 * @returns {number} - Match score percentage (0-100)
 */
const calculateMatchScore = (profileId, queryResults, searchCxt, requirements) => {
  try {
    // Find the query result for this profile
    const profileResult = queryResults.find(result => result.profile_id === profileId);
    if (!profileResult) {
      console.log(`No query result found for profile ${profileId}`);
      return 50; // Default score
    }

    // If profile is marked as empty, return low score
    if (profileResult.is_empty_profile) {
      return 20;
    }

    // Get total number of requirements
    const totalRequirements = requirements.length;
    if (totalRequirements === 0) {
      return profileResult.score || 50; // Use API score if no requirements to match against
    }

    // Get auto_macro_map from search context
    const autoMacroMap = searchCxt.auto_macro_map || {};
    
    // Count matched requirements based on macro_ids and auto_macro_map
    let matchedRequirements = 0;
    if (profileResult.macro_ids && profileResult.macro_ids.length > 0) {
      // For each macro_id in the profile result
      profileResult.macro_ids.forEach(macroId => {
        // Find the corresponding requirement bool_str in auto_macro_map
        const requirementBoolStr = autoMacroMap[macroId];
        if (requirementBoolStr) {
          // Check if this requirement maps to one of our original requirements
          const matchingRequirement = requirements.find(req => 
            req.bool_str === requirementBoolStr
          );
          if (matchingRequirement) {
            matchedRequirements++;
          }
        }
      });
    }

    // Calculate percentage match
    const percentageMatch = (matchedRequirements / totalRequirements) * 100;
    
    // Combine with API score for better accuracy
    const combinedScore = (percentageMatch * 0.7) + ((profileResult.score || 50) * 0.3);
    
    console.log(`Match score for ${profileId}:`, {
      matched: matchedRequirements,
      total: totalRequirements,
      percentage: percentageMatch.toFixed(1),
      apiScore: profileResult.score,
      combined: combinedScore.toFixed(1)
    });

    return combinedScore;

  } catch (error) {
    console.error('Error calculating match score for profile', profileId, error);
    // Fallback to API score or default
    const profileResult = queryResults.find(result => result.profile_id === profileId);
    return profileResult?.score || 50;
  }
};

/**
 * Calculate total work experience years, handling overlapping periods
 * @param {Array} workexp - Array of work experience objects
 * @returns {number} - Total experience years without double-counting overlaps
 */
const calculateExperienceYears = (workexp) => {
  if (!workexp || workexp.length === 0) return 0;
  
  // Convert work periods to date ranges
  const periods = [];
  const now = new Date();
  
  workexp.forEach(exp => {
    if (!exp.duration?.start_date) return;
    
    const start = new Date(exp.duration.start_date);
    let end;
    
    if (exp.duration.to_present) {
      end = now;
    } else if (exp.duration.end_date) {
      end = new Date(exp.duration.end_date);
    } else {
      return; // Skip invalid periods
    }
    
    // Only add valid periods (start before end)
    if (start < end) {
      periods.push({ start, end });
    }
  });
  
  if (periods.length === 0) return 0;
  
  // Sort periods by start date
  periods.sort((a, b) => a.start - b.start);
  
  // Merge overlapping periods
  const merged = [periods[0]];
  
  for (let i = 1; i < periods.length; i++) {
    const current = periods[i];
    const last = merged[merged.length - 1];
    
    // If current period overlaps with the last merged period
    if (current.start <= last.end) {
      // Extend the last period if current ends later
      if (current.end > last.end) {
        last.end = current.end;
      }
    } else {
      // No overlap, add as new period
      merged.push(current);
    }
  }
  
  // Calculate total years from merged periods
  let totalYears = 0;
  merged.forEach(period => {
    const years = (period.end - period.start) / (1000 * 60 * 60 * 24 * 365.25); // Account for leap years
    totalYears += Math.max(0, years);
  });
  
  return totalYears;
};

/**
 * Fetch match reason summary for a candidate profile
 * @param {string} candidateId - Profile ID of the candidate
 * @param {string} userId - User ID for the request
 * @param {string} searchCriteria - Search criteria summary
 * @returns {Promise<string>} - Match reason summary
 */
export const fetchMatchReason = async (candidateId, userId, searchCriteria) => {
  const cacheKey = `match-reason-${candidateId}-${userId}`;
  
  if (responseCache.has(cacheKey)) {
    console.log('Using cached match reason');
    return responseCache.get(cacheKey);
  }

  try {
    const payload = {
      reset_context: true,
      joid: userId,
      qna_profile_info: {
        icpid: '',
        questions: [
          'How does this candidate match the search requirement? Be factual, and without buzzwords. Try answering in less than 150 words and exclusively in bullet points. Use up to 5 bullet points and use short sentences. Add labels for each bullet point with colons before the content of the bullet point. For each bullet point, express if the candidate profile matches the search requirement. An example of a bullet point could be: "- **Keywords**: Candidate has experience with "Cloud" and "API integration," aligning with the keyword requirements for Cloud Solutions.". In case of overall experience, express in years instead of months. Ensure that your answer is unbiased and does not rely on stereotypes. Do not use superlatives like "perfect fit" or declare abilities without assessment, e.g. "highly technical".'
        ],
        prid: candidateId,
        search_criteria: searchCriteria,
        is_sandbox_collection: false,
        ai_summary_call: true
      }
    };

    console.log('Fetching match reason for candidate:', candidateId);

    const response = await fetch('https://search.findem.ai/pub/api/copilot-profile', {
      method: 'POST',
      credentials: 'include',
      headers: {
        'accept': '*/*',
        'content-type': 'application/json',
        'cache-control': 'no-cache',
        'pragma': 'no-cache'
      },
      body: JSON.stringify(payload),
      mode: 'cors'
    });

    if (!response.ok) {
      throw new Error(`Match reason fetch failed: ${response.status} ${response.statusText}`);
    }

    const result = await response.json();
    
    // Extract the assistant's response from message_history
    let matchReason = 'Match analysis not available.';
    
    if (result.qna_profile_response?.content) {
      matchReason = result.qna_profile_response.content.split('\n')
      .map((line) => line.replace('- ', '').replace(/\*\*(.*?)\*\*/i, "<strong>$1</strong>")).join('\n')
    }

    console.log('Match reason fetched successfully for:', candidateId);
    responseCache.set(cacheKey, matchReason);
    return matchReason;

  } catch (error) {
    console.error('Error fetching match reason:', error);
    return 'Unable to generate match analysis at this time. Please try again later.';
  }
};

/**
 * Clear the response cache (useful for testing or when starting fresh)
 */
export const clearCache = () => {
  responseCache.clear();
  console.log('Hiring agent API cache cleared');
};